import { Avatar, Tooltip } from '@affine/component';
import { useCurrentServerService } from '@affine/core/components/providers/current-server-scope';
import { useI18n } from '@affine/i18n';
import { useLiveData } from '@toeverything/infra';
import {
  type ComponentType,
  type CSSProperties,
  useLayoutEffect,
  useMemo,
} from 'react';

import { useCachedUserAvatarUrl } from '../hooks/use-cached-user-avatar-url';
import { PublicUserService } from '../services/public-user';
import * as styles from './public-user.css';

/** The public profile of a user on the current server, loaded when asked for. */
export function usePublicUser(id: string | null) {
  const serverService = useCurrentServerService();
  const publicUser = useMemo(
    () => serverService?.scope.get(PublicUserService),
    [serverService]
  );
  useLayoutEffect(() => {
    if (publicUser && id) publicUser.revalidate(id);
  }, [id, publicUser]);
  const user = useLiveData(id ? publicUser?.publicUser$(id) : null);
  const isLoading = useLiveData(id ? publicUser?.isLoading$(id) : null);
  return { user, isLoading: Boolean(isLoading) };
}

export const PublicUserLabel = ({
  id,
  size = 20,
  showName = true,
  tooltip: NameTip,
  align = 'baseline',
  showTooltip = true,
  truncate = false,
}: {
  id: string;
  size?: number;
  showName?: boolean;
  tooltip?: ComponentType<{ userName: string }>;
  align?: CSSProperties['alignItems'];
  showTooltip?: boolean;
  /** The name ends with "…" instead of growing past its container. */
  truncate?: boolean;
}) => {
  const { user, isLoading } = usePublicUser(id);
  const t = useI18n();
  const cachedAvatarUrl = useCachedUserAvatarUrl({
    userId: user?.removed ? null : user?.id,
    avatarUrl: user?.removed ? null : user?.avatar,
  });
  const userName =
    user && !user.removed
      ? user.name || t['Unknown User']()
      : t['Unknown User']();

  if (isLoading && !user) {
    return <span className={styles.publicUserLabelLoading}>...</span>;
  }

  if (user?.removed) {
    return showName ? (
      <span className={styles.publicUserLabelRemoved}>
        {t['Unknown User']()}
      </span>
    ) : (
      <Avatar
        size={size}
        name={t['Unknown User']()}
        className={styles.publicUserLabelAvatar}
      />
    );
  }

  return (
    <Tooltip
      content={showTooltip && NameTip ? <NameTip userName={userName} /> : null}
    >
      <span
        className={styles.publicUserLabel}
        style={{ alignItems: align }}
        data-truncate={truncate || undefined}
      >
        <Avatar
          url={cachedAvatarUrl}
          name={userName}
          size={size}
          className={styles.publicUserLabelAvatar}
          data-show-name={showName}
          avatarProps={{ role: 'img', 'aria-label': userName }}
          avatarTooltipOptions={
            !showTooltip
              ? { content: null }
              : NameTip
                ? undefined
                : { content: userName }
          }
        />
        {showName && user?.name ? (
          <span className={truncate ? styles.publicUserLabelName : undefined}>
            {user.name}
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
};
